import React, { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Alert, AlertTitle, Box, SvgIcon, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ConversationMessageContext } from '@klab-berlin/api-sdk/lib/types/common/Conversation';
import { borderRadius, palette, spaces } from '../../../assets/styles/themes/tokens';
import getMuiIcon from '../../../utils/getMuiIcon';
import AIAssistantTextEditorCopyButton from '../shared/TextEditor/AIAssistantTextEditorCopyButton';
import AIAssistantTextEditorExportButton, {
  TextEditorDownloadFormat,
} from '../shared/TextEditor/AIAssistantTextEditorExportButton';
import PodcastAudioPlayer from './PodcastAudioPlayer';
import { aiAssistantEditorContext } from '../../../state/AIAssistantEditorProvider';
import { convertMarkdownToHtml } from '../../utils/contentConverter';
import { getCurrentConversation } from '../../../reducers/aiAssistant.selectors';
import { getAIAssistantPageSource } from '../../../utils/getAIAssistantPageSource';
import useRouter from '../../../utils/useRouter';
import services from '../../../services';

const containerStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: spaces.lg,
  padding: spaces.lg,
  width: '100%',
  boxSizing: 'border-box',
};

const transcriptHeaderStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: spaces.sm,
  flexWrap: 'wrap',
};

const transcriptTitleStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: spaces.xs,
};

const transcriptActionsStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: spaces.sm,
};

const transcriptCardStyles = {
  width: '100%',
  padding: spaces.md,
  border: `1px solid ${palette.divider}`,
  borderRadius: borderRadius.md,
  background: palette.background.paperElevation2,
  boxSizing: 'border-box',
};

// The SDK message context type does not declare the podcast field yet.
type PodcastMessageContext = ConversationMessageContext & { generatedPodcastId?: string };

const PodcastDetails: React.FC = () => {
  const { t } = useTranslation();
  const { podcastDetails } = useContext(aiAssistantEditorContext);
  const currentConversation = useSelector(getCurrentConversation);
  const currentConversationId = currentConversation?.data?.id;
  const { match: { location: { pathname } } } = useRouter();

  const transcriptHtml = useMemo(
    () => (podcastDetails ? convertMarkdownToHtml(podcastDetails.transcript) : ''),
    [podcastDetails]
  );

  const trackTranscriptExport = (
    exportType: 'download' | 'print',
    exportFormat: TextEditorDownloadFormat
  ) => {
    if (!currentConversationId) return;
    services.track.ampliTrackTextEditorFileExportedEvent({
      conversationId: currentConversationId,
      exportType,
      exportFormat,
      feature: ['podcast generation'],
    });
  };

  const handleTranscriptDownload = (format: TextEditorDownloadFormat) => {
    trackTranscriptExport('download', format);
  };

  const handleTranscriptPrint = () => {
    trackTranscriptExport('print', 'pdf');
  };

  const trackAudioActionPerformed = useCallback(
    (type: 'play audio' | 'pause audio') => {
      const conversationId = currentConversation?.data?.id;
      const messages = currentConversation?.data?.messages;
      if (!conversationId || !messages || !podcastDetails) return;

      const requestIndex = messages.findIndex((msg) => (
        msg.role === 'assistant'
        && (msg.context as PodcastMessageContext | undefined)?.generatedPodcastId === podcastDetails.id
      ));
      const requestMessage = requestIndex === -1 ? null : messages[requestIndex];
      if (!requestMessage) return;

      // The user prompt that requested the podcast is the last user message before the response.
      const userMessage = messages
        .slice(0, requestIndex)
        .reverse()
        .find((message) => message.role === 'user') ?? null;

      services.track.ampliTrackAIAssistantActionPerformedEvent({
        conversationId,
        requestId: requestMessage.id,
        pageSource: getAIAssistantPageSource(pathname),
        feature: ['podcast generation'],
        type,
        userPrompt: userMessage?.content[0]?.text,
      });
    },
    [currentConversation, podcastDetails, pathname]
  );

  const handleAudioPlayTrack = useCallback(
    () => trackAudioActionPerformed('play audio'),
    [trackAudioActionPerformed]
  );

  const handleAudioPauseTrack = useCallback(
    () => trackAudioActionPerformed('pause audio'),
    [trackAudioActionPerformed]
  );

  if (!podcastDetails) {
    return null;
  }

  const metadataLabel = [
    podcastDetails.subject,
    podcastDetails.grade,
    podcastDetails.language,
    podcastDetails.format,
  ].join(' • ');

  const showAudioError = Boolean(podcastDetails.audioGenerationFailed) || !podcastDetails.audioUrl;

  return (
    <Box sx={containerStyles} data-testid='podcast-details'>
      <Typography variant='body2' sx={{ color: palette.text.secondary }}>
        {metadataLabel}
      </Typography>

      {showAudioError ? (
        <Alert severity='warning' data-testid='podcast-audio-error-alert' sx={{ zIndex: 'auto' }}>
          <AlertTitle sx={{ color: palette.warning.main }}>
            {t('Audio could not be loaded')}
          </AlertTitle>
          <Typography variant='body2' sx={{ color: palette.warning.main }}>
            {t('The audio file could not be loaded. However, the transcript is still available to you.')}
          </Typography>
        </Alert>
      ) : (
        <PodcastAudioPlayer
          src={podcastDetails.audioUrl}
          durationLabelFallback={podcastDetails.durationLabel}
          onPlay={handleAudioPlayTrack}
          onPause={handleAudioPauseTrack}
        />
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.sm }}>
        <Box sx={transcriptHeaderStyles}>
          <Box sx={transcriptTitleStyles}>
            <SvgIcon
              component={getMuiIcon('transcript')}
              aria-hidden='true'
              sx={{ color: palette.text.secondary }}
            />
            <Typography variant='subtitle1' sx={{ color: palette.text.primary }}>
              {t('Transcript')}
            </Typography>
          </Box>
          <Box sx={transcriptActionsStyles}>
            <AIAssistantTextEditorCopyButton
              content={transcriptHtml}
              dataTestId='podcast-details-copy-transcript'
              sx={{ color: palette.primary.main }}
            />
            <AIAssistantTextEditorExportButton
              variant='outlined'
              label={t('Export transcript')}
              id='podcast-details-export-transcript-button'
              dataTestId='podcast-details-export-transcript'
              showShare={false}
              showNotification={false}
              menuAnchorOrigin='right'
              content={transcriptHtml}
              onDownload={handleTranscriptDownload}
              onPrint={handleTranscriptPrint}
            />
          </Box>
        </Box>

        <Box sx={transcriptCardStyles}>
          <Typography
            variant='body2'
            component='div'
            sx={{ color: palette.text.primary, whiteSpace: 'pre-wrap' }}
          >
            {podcastDetails.transcript}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PodcastDetails;
